import { useQuery } from "@apollo/client";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { Alert, Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { FacilityAccumulationSummaryQuery } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import pluralize from "pluralize";
import { Link as RouterLink } from "react-router-dom";
import { formatNumber } from "util/formatNumber";

import { FACILITY_ACCUMULATION_SUMMARY_QUERY } from "../../Containers/api";
import { useWasteFacilityState } from "./WasteFacilityContext";

type AccumulationSummary = NonNullable<
  NonNullable<FacilityAccumulationSummaryQuery["facility"]>["accumulationSummary"]
>;

/**
 * Containers & accumulation summary for the waste facility dashboard: stat
 * cards for the active container count and the on-site hazardous total vs
 * the generator-status cap, plus a link to the facility-scoped container
 * list.
 */
export const WasteContainersTile = () => {
  const { tenantId } = useTenant();
  const { effectiveWasteFacilityId } = useWasteFacilityState();

  const { data, loading } = useQuery(FACILITY_ACCUMULATION_SUMMARY_QUERY, {
    variables: { facilityId: effectiveWasteFacilityId },
    skip: !effectiveWasteFacilityId,
    // Revalidate on mount so the on-site total reflects containers added from
    // other routes (create-container's refetch only hits this query while this
    // tile is mounted); cache-first alone leaves a stale total until hard refresh.
    fetchPolicy: "cache-and-network",
  });
  const summary = data?.facility?.accumulationSummary;

  return (
    <DashboardTile size={{ xs: 12 }}>
      <DashboardTile.Header title="Containers & accumulation" />
      <DashboardTile.ContentArea>
        <Stack spacing={2} alignItems="stretch" sx={{ width: "100%" }}>
          {loading && summary == null ? (
            <Stack direction="row" spacing={2}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} variant="rounded" height={120} sx={{ flex: 1 }} />
              ))}
            </Stack>
          ) : (
            <AccumulationStatRow summary={summary} />
          )}
          {summary && <SummaryCallouts summary={summary} />}
          <Button
            component={RouterLink}
            to={`/o/${tenantId}/waste/containers?facilityId=${effectiveWasteFacilityId}`}
            variant="text"
            size="small"
            endIcon={<ArrowForward />}
            sx={{ alignSelf: "flex-start" }}
          >
            View containers
          </Button>
        </Stack>
      </DashboardTile.ContentArea>
    </DashboardTile>
  );
};

const StatCard = ({
  value,
  caption,
  subCaption,
  valueColor = "text.primary",
  subCaptionColor = "text.secondary",
}: {
  value: string;
  caption: string;
  subCaption?: string;
  valueColor?: string;
  subCaptionColor?: string;
}) => (
  // The shared grey tile box centers its text and fixes a tighter corner
  // radius; the stat cards want left-aligned data, vertical centering, and
  // softer corners, so they carry their own box with the same grey.
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      minHeight: 120,
      px: 2,
      py: 1,
      backgroundColor: (theme) => theme.palette.grey50.main,
      borderRadius: 3,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }}
  >
    <Stack spacing={0.5}>
      <Typography variant="h4" color={valueColor}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
      {subCaption && (
        <Typography variant="caption" color={subCaptionColor}>
          {subCaption}
        </Typography>
      )}
    </Stack>
  </Box>
);

/** Whole kilograms for display — converted totals carry noisy decimals. */
const formatKg = (kg: number) => `${formatNumber(Math.round(kg))} kg`;

/**
 * The kg-on-site stat: the big number is the hazardous mass currently on
 * site; the bottom caption names the cap it's judged against — SQG/VSQG get
 * their cap, LQGs have none (time-limited instead), and an unresolvable
 * generator status shows no cap (the callout below prompts for setup).
 */
const OnSiteTotalStatCard = ({ summary }: { summary: AccumulationSummary | undefined }) => {
  const totalOnSiteKg = (summary?.nonAcuteOnSiteKg ?? 0) + (summary?.acuteOnSiteKg ?? 0);

  let caption = "On-site total";
  if (summary?.onSiteCapKg != null) {
    caption = `On-site total - ${formatKg(summary.onSiteCapKg)} cap`;
  } else if (summary?.generatorStatus === "LARGE_QUANTITY_GENERATOR") {
    caption = "On-site total - no cap (LQG)";
  } else if (summary) {
    caption = "On-site total - no cap set";
  }

  const acuteOver =
    summary?.acuteOnSiteCapKg != null && summary.acuteOnSiteKg > summary.acuteOnSiteCapKg;

  return (
    <StatCard
      value={formatKg(totalOnSiteKg)}
      caption={caption}
      // The acute line keeps its decimals: it's judged against a 1 kg cap,
      // where rounding 0.5 kg up to "1 kg" would misread as at-cap.
      subCaption={
        summary?.acuteOnSiteCapKg != null
          ? `Acute: ${formatNumber(summary.acuteOnSiteKg)} kg of ${formatNumber(
              summary.acuteOnSiteCapKg
            )} kg cap`
          : undefined
      }
      valueColor={summary?.overCap ? "error.main" : "text.primary"}
      subCaptionColor={acuteOver ? "error.main" : "text.secondary"}
    />
  );
};

const SummaryCallouts = ({ summary }: { summary: AccumulationSummary }) => (
  <>
    {summary.generatorStatus == null && (
      <Alert severity="info">Set your generator status to see the facility's on-site cap.</Alert>
    )}
    {summary.overCap && <Alert severity="error">Over the on-site accumulation cap</Alert>}
    {summary.assumedDensityContainerCount > 0 && (
      <Typography variant="caption" color="warning.main">
        {summary.assumedDensityContainerCount}{" "}
        {pluralize("container", summary.assumedDensityContainerCount)} converted using the density
        of water — totals may be inaccurate until{" "}
        {summary.assumedDensityContainerCount === 1 ? "its" : "their"} waste{" "}
        {pluralize("stream", summary.assumedDensityContainerCount)}{" "}
        {summary.assumedDensityContainerCount === 1 ? "has" : "have"} density information
      </Typography>
    )}
  </>
);

/** A count that reads as 0 rather than blank when the summary has not loaded. */
const count = (value: number | undefined) => value ?? 0;

/**
 * Non-zero counts are the ones worth looking at, so they take a semantic color
 * and everything else stays neutral. A helper rather than a ternary per card:
 * the rule is the same on every one of them.
 */
const emphasizeIfAny = (value: number | undefined, color: string) =>
  count(value) > 0 ? color : "text.primary";

/**
 * The facility's on-site numbers at a glance. Its own component because each
 * card carries a "highlight when non-zero" rule, and eight of those inline made
 * the tile itself hard to read.
 */
const AccumulationStatRow = ({ summary }: { summary: AccumulationSummary | undefined }) => (
  <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
    <StatCard
      value={String(count(summary?.activeContainerCount))}
      caption={`Active ${pluralize("container", count(summary?.activeContainerCount))}`}
    />
    <StatCard
      value={String(count(summary?.approachingDeadlineContainerCount))}
      caption="Approaching deadline"
      valueColor={emphasizeIfAny(summary?.approachingDeadlineContainerCount, "warning.main")}
    />
    <StatCard
      value={String(count(summary?.overdueContainerCount))}
      caption="Overdue"
      valueColor={emphasizeIfAny(summary?.overdueContainerCount, "error.main")}
    />
    <OnSiteTotalStatCard summary={summary} />
    <StatCard
      value={String(count(summary?.overThresholdAreaCount))}
      caption={`Satellite ${pluralize("area", count(summary?.overThresholdAreaCount))} over limit`}
      valueColor={emphasizeIfAny(summary?.overThresholdAreaCount, "error.main")}
    />
    {/* Warning, not error: the vendor re-weighing or re-classifying at
            the TSDF is normal and the manifest is the record of what
            actually left. This is something to review, not to fix. */}
    <StatCard
      value={String(count(summary?.discrepantShipmentCount))}
      caption={`${pluralize("Shipment", count(summary?.discrepantShipmentCount))} differing from manifest`}
      valueColor={emphasizeIfAny(summary?.discrepantShipmentCount, "warning.main")}
    />
  </Stack>
);
